import React, { useState, useEffect } from 'react';
import { Button, Steps } from 'antd';
import AppLayout from './AppLayout';
import { TLesson } from '../../models/courses/lesson';
import { TLessonProgress } from '../../models/user/UserProgression';
import { LoadingOutlined } from '@ant-design/icons';
import './CourseLayout.css';
import { useTranslation } from 'react-i18next';
import { useTilmiAuth } from '../../hooks/useTilmiAuth';
import { CODIFICATION_TYPE_USERS } from '../../constant/codification';

interface CourseLayoutProps {
    children: React.ReactNode;
    title: string;
    lessons: TLesson[];
    progression: TLessonProgress[];
    currentStep: number;
    previous?: () => void;
    next?: () => void;
    goToStep?: (stepIndex: number) => void;
    finishCourse?: () => void;
    isLoading?: boolean;
    isSaving?: boolean;
    onBackNavigation?: () => void;
    showBackButton?: boolean;
}

const { Step } = Steps;

const CourseLayout: React.FC<CourseLayoutProps> = ({
    title,
    children,
    lessons,
    progression,
    currentStep,
    previous,
    next,
    goToStep,
    finishCourse,
    isLoading = false,
    isSaving = false,
    onBackNavigation,
    showBackButton = false,
}) => {
    const { t } = useTranslation();
    const [isMobile, setIsMobile] = useState(false);
    const [isTablet, setIsTablet] = useState(false);
    const { getCodeTypeUser } = useTilmiAuth();
    const codeType = getCodeTypeUser();
    const isTeacher = codeType === CODIFICATION_TYPE_USERS.TEACHER;
    const isAdmin = codeType === CODIFICATION_TYPE_USERS.ADMIN;
    const isCourseOp = codeType === CODIFICATION_TYPE_USERS.COURSE_OP;
    const isPrivileged = isTeacher || isAdmin || isCourseOp;

    useEffect(() => {
        const checkScreenSize = () => {
            const width = window.innerWidth;
            setIsMobile(width < 768);
            setIsTablet(width >= 768 && width < 1024);
        };
        checkScreenSize();
        window.addEventListener('resize', checkScreenSize);
        return () => window.removeEventListener('resize', checkScreenSize);
    }, []);

    // Footer height used to offset the main content so it never hides behind the fixed footer
    const FOOTER_HEIGHT = isMobile || isTablet ? 96 : 60;

    const footerContent = (
        <div className="course-progress-footer">
            <div className="course-progress-footer__inner">
                <div className="course-progress-footer__slot course-progress-footer__slot--prev">
                    {previous && (
                        <Button
                            onClick={previous}
                            type="default"
                            size={isMobile ? 'small' : 'middle'}
                            className="course-progress-footer__button"
                        >
                            {t('courses.progress.prev', 'Précédent')}
                        </Button>
                    )}
                </div>

                <div className="course-progress-footer__steps">
                    {isMobile || isTablet ? (
                        <div className="course-progress-steps-compact">
                            <span className="course-progress-steps-compact__label">
                                {t('courses.progress.currentLessonShort', 'Exercice')}
                            </span>
                            <span className="course-progress-steps-compact__value">
                                {currentStep + 1}
                                <span className="course-progress-steps-compact__separator">/</span>
                                {lessons.length}
                            </span>
                        </div>
                    ) : (
                        <>
                            {(() => {
                                const firstIncompleteIndex = (lessons || []).findIndex(
                                    (l) => !progression.some((p) => p.lesson_id === l.id)
                                );

                                const handleStepChange = (targetIndex: number) => {
                                    if (!goToStep) return;
                                    if (isPrivileged) {
                                        goToStep(targetIndex);
                                        return;
                                    }

                                    const targetLesson = lessons[targetIndex];
                                    const isCompleted = progression.some((p) => p.lesson_id === targetLesson?.id);
                                    const isNext = firstIncompleteIndex === targetIndex;

                                    if (isCompleted || isNext) {
                                        goToStep(targetIndex);
                                    }
                                };

                                return (
                                    <Steps
                                        current={currentStep}
                                        size="small"
                                        direction="horizontal"
                                        onChange={handleStepChange}
                                        className="course-progress-steps"
                                    >
                                        {(lessons || []).map((lesson, idx) => {
                                            const isCompleted = progression.some((prog) => prog.lesson_id === lesson.id);
                                            const isEnabled = isPrivileged || isCompleted || idx === firstIncompleteIndex;

                                            return (
                                                <Step
                                                    key={lesson.id}
                                                    title={lesson.name}
                                                    status={isCompleted ? 'finish' : 'wait'}
                                                    disabled={!isEnabled}
                                                />
                                            );
                                        })}
                                    </Steps>
                                );
                            })()}
                        </>
                    )}
                </div>

                <div className="course-progress-footer__slot course-progress-footer__slot--next">
                    {currentStep === lessons.length - 1 && finishCourse ? (
                        <Button
                            type="primary"
                            onClick={finishCourse}
                            size={isMobile ? 'small' : 'middle'}
                            icon={isSaving ? <LoadingOutlined spin /> : undefined}
                            className="course-progress-footer__button course-progress-footer__button--primary"
                        >
                            {t('courses.progress.finish', 'Terminer')}
                        </Button>
                    ) : (
                        <Button
                            type="primary"
                            onClick={next}
                            disabled={!next}
                            size={isMobile ? 'small' : 'middle'}
                            icon={isSaving ? <LoadingOutlined spin /> : undefined}
                            className="course-progress-footer__button course-progress-footer__button--primary"
                        >
                            {t('courses.progress.next', 'Suivant')}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <AppLayout
            title={title}
            footer={footerContent}
            loading={isLoading}
            onBackNavigation={onBackNavigation}
            showBackButton={showBackButton}
        >
            {/* Wrapper to ensure full-height layout and bottom padding equal to footer height */}
            <div
                style={{
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <div style={{ flex: 1, paddingBottom: FOOTER_HEIGHT }}>
                    {children}
                </div>
            </div>
        </AppLayout>
    );
};

export default CourseLayout;
